import type { ReactNode } from 'react';
import type {
  DropdownChangeFn,
  DropdownFilterCallback,
} from '../../Dropdown/Dropdown';
import type { DropdownOption } from '../../Dropdown/DropdownOption/DropdownOption';

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EMPTY_ARRAY } from '../../../lib/globals';

import Dropdown from '../../Dropdown/Dropdown';
import Loader from '../../Loader/Loader';

/**
 * Update filters for data grid.
 */
export type HeaderFilterUpdateFiltersFn = (
  id: string,
  value: any,
  filterType?: string,
  label?: ReactNode,
) => void;

interface HeaderFilterDatasourceProps {
  /**
   * HeaderFilterDatasource id
   */
  id?: string;
  /**
   * HeaderFilterDatasource handle filter function
   */
  handleFilters?: HeaderFilterUpdateFiltersFn;
  /**
   * HeaderFilterDatasource value
   */
  value?: string;
  /**
   * HeaderFilterDatasource position of the dropdown if is going from left
   */
  dropdownLeftPosition?: boolean;
  /**
   * Extra filter options
   */
  extraOptions?: DropdownOption[];
  /**
   * HeaderFilterDatasource data test id
   */
  testId?: string;
  /**
   * HeaderFilterDatasource validation
   */
  validation?: {
    relationContenttype?: string;
    relationMultiple?: boolean;
  };
  /**
   * HeaderFilterDatasource callback to get filtered data from
   */
  filterDatasourceFetch?: (
    query?: string,
  ) => Promise<DropdownOption[]> | undefined;
  /**
   * HeaderFilterDatasource options
   */
  options?: DropdownOption[];
  /**
   * HeaderFilterDatasource disabled
   */
  disabled?: boolean;
}

const HeaderFilterDatasource = ({
  id = '',
  value = '',
  options = EMPTY_ARRAY,
  handleFilters,
  dropdownLeftPosition = false,
  testId = '',
  disabled,
  extraOptions,
  filterDatasourceFetch,
}: HeaderFilterDatasourceProps) => {
  const { t } = useTranslation();
  const [currentOptions, setCurrentOptions] = useState(options);
  const [defaultOptions, setDefaultOptions] = useState<DropdownOption[]>([]);

  const handleOnChange: DropdownChangeFn = (e) => {
    if (e.target.value) {
      setCurrentOptions([
        { value: e.target.value, label: e.option?.label || e.target.value },
        ...defaultOptions.filter((el) => el.value !== e.target.value),
      ]);
    } else {
      setCurrentOptions(defaultOptions);
    }
    handleFilters?.(id, e);
  };

  const filterCallback: DropdownFilterCallback = useCallback(
    async (query, options, setIsLoading, currentValue) => {
      setIsLoading(true);

      let resOptions: DropdownOption[] | undefined = options;

      if (query !== currentValue && filterDatasourceFetch) {
        const isInitOnEmpty = query === '_initOnEmpty';
        const isInitOnEmptyFirst = isInitOnEmpty && !defaultOptions?.length;

        if (isInitOnEmpty) {
          if (isInitOnEmptyFirst) {
            resOptions = await filterDatasourceFetch();
            if (!resOptions?.length) {
              setIsLoading(false);
              return [];
            }

            // Case: add default options on init fetch
            setDefaultOptions(resOptions);
          } else if (!value) {
            // Case: show default options instead another fetch
            resOptions = defaultOptions;
          }
        } else if (!isInitOnEmpty) {
          resOptions = await filterDatasourceFetch(query);
        }
      }

      setIsLoading(false);

      return resOptions || [];
    },
    [defaultOptions, filterDatasourceFetch, value],
  );
  return (
    <Dropdown
      testId={testId}
      onChange={handleOnChange}
      name={id}
      options={currentOptions}
      value={value}
      nullable={true}
      loadingIcon={
        <div className="h-10 overflow-hidden flex justify-center items-center">
          <Loader type="spinner-grid" size="tiny" />
        </div>
      }
      renderEmpty={() => (
        <div className="p-2 text-slate-400/80 text-sm text-center">
          {t('Global.NoData')}
        </div>
      )}
      emptyOptions={
        <div className="p-2 text-slate-400/80 text-sm text-center">
          {t('Global.NoData')}
        </div>
      }
      filterCallback={filterCallback}
      filterCallbackOnEmpty={true}
      disabled={disabled}
      debounceTime={500}
      additionalContainerClasses={'h-10'}
      additionalClasses={'relative w-full flex flex-col'}
      additionalDropdownClasses={`w-auto min-w-full top-12 ${
        dropdownLeftPosition ? 'left-0' : 'right-0'
      }`}
      extraOptions={extraOptions}
    />
  );
};

export default HeaderFilterDatasource;
